import { apiHeaders } from "../api/headers";
import { useEffect, useState } from "react";
import { DownloadIcon } from "./Icons";
import { Empty, ErrorState, Loading } from "./States";

type Probe = "checking" | "ready" | "missing" | "error";

/**
 * The original uploaded file, so a reviewer can check an extracted value
 * against the page it came from.
 *
 * The availability probe is a HEAD request rather than an optimistic render:
 * the endpoint answers with JSON on failure, and an <iframe> pointed at a 404
 * displays that JSON as the "document", which looks like a corrupted file
 * rather than a missing one. Extractions also outlive their originals — a
 * storage lifecycle rule can delete the file while the derived rows remain —
 * so "no original to show" is a normal state that needs saying properly.
 */
export function DocumentViewer({
  src,
  filename,
  height = 720,
  missingTitle = "No original file for this document",
  missingHint = "The extraction is on record but the source file is not in storage — it may have been removed by a retention rule, or ingested without a storage key.",
}: {
  src: string;
  filename?: string | null;
  height?: number;
  /**
   * What a 404 means here.
   *
   * The defaults describe a document whose extraction outlived its file. The
   * storage preview uses the same component for a different question -- "is
   * there an object under this key" -- where that wording asserts something
   * untrue, since no document exists either way.
   */
  missingTitle?: string;
  missingHint?: string;
}) {
  const [probe, setProbe] = useState<Probe>("checking");
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProbe("checking");
    setDetail(null);

    fetch(src, { method: "HEAD", headers: apiHeaders() })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          setProbe("ready");
          return;
        }
        if (res.status === 404) {
          setProbe("missing");
          return;
        }

        // A HEAD carries no body, so the reason has to be asked for
        // separately. Worth the second request: the difference between
        // "that key is not there" and "the bucket is unreachable" is
        // the difference between fixing a typo and fixing a deployment,
        // and "the file service returned 502" says neither.
        setProbe("error");
        setDetail(`The file service returned ${res.status}.`);
        try {
          const body = await fetch(src, { headers: apiHeaders() }).then((r) =>
            r.json(),
          );
          if (!cancelled && body?.message) setDetail(body.message);
        } catch {
          /* keep the status-line fallback above */
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setProbe("error");
        setDetail(e instanceof Error ? e.message : String(e));
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (probe === "checking") return <Loading what="the original document" />;

  if (probe === "missing") {
    return <Empty title={missingTitle} hint={missingHint} />;
  }

  if (probe === "error") {
    return (
      <ErrorState message={detail ?? "Could not reach the file service."} />
    );
  }

  return (
    <div className="viewer">
      <div className="viewer-bar">
        <span className="muted mono">{filename ?? "original document"}</span>
        <span className="viewer-actions">
          <a href={src} target="_blank" rel="noreferrer">
            Open in new tab
          </a>
          {/* A real anchor download, not a scripted save: this is served
                        from our own origin with a Content-Disposition already set. */}
          <a
            href={src}
            download={filename ?? undefined}
            className="viewer-download"
          >
            <DownloadIcon />
            Download
          </a>
        </span>
      </div>
      {/* The browser's built-in PDF/image viewer. Deliberately not a
                bundled PDF renderer: that would add roughly a megabyte of
                JavaScript to show a file the browser already renders, and the
                CSP would have to allow a worker for it. */}
      <iframe
        className="viewer-frame"
        src={src}
        title={`Original document${filename ? `: ${filename}` : ""}`}
        style={{ height }}
      />
    </div>
  );
}
